/**
 * @fileoverview Tres botones grandes para votar: acuerdo / desacuerdo /
 * pasar. Soporta estado seleccionado (cuando ya hay voto previo) y
 * envío en curso (spinner).
 */

import { ChevronsRight, ThumbsDown, ThumbsUp } from "lucide-react";

import { LoadingSpinner } from "./LoadingSpinner";

/**
 * @typedef {"acuerdo" | "desacuerdo" | "pasar"} Valor
 */

/**
 * @param {{
 *   onVotar: (valor: Valor) => void,
 *   seleccionado: Valor | null,
 *   enviando: boolean,
 *   valorEnviando: Valor | null,
 *   disabled?: boolean,
 * }} props
 */
export function VoteButtons({ onVotar, seleccionado, enviando, valorEnviando, disabled }) {
    /**
     * @type {Array<{
     *   valor: Valor,
     *   etiqueta: string,
     *   icono: JSX.Element,
     *   colorBg: string,
     *   colorBgSelected: string,
     *   colorBorder: string,
     *   colorText: string,
     *   atajo: string,
     * }>}
     */
    const opciones = [
        {
            valor: "acuerdo",
            etiqueta: "De acuerdo",
            icono: <ThumbsUp size={28} aria-hidden />,
            colorBg: "bg-vote-acuerdo-soft",
            colorBgSelected: "bg-vote-acuerdo",
            colorBorder: "border-vote-acuerdo",
            colorText: "text-vote-acuerdo",
            atajo: "A",
        },
        {
            valor: "desacuerdo",
            etiqueta: "En desacuerdo",
            icono: <ThumbsDown size={28} aria-hidden />,
            colorBg: "bg-vote-desacuerdo-soft",
            colorBgSelected: "bg-vote-desacuerdo",
            colorBorder: "border-vote-desacuerdo",
            colorText: "text-vote-desacuerdo",
            atajo: "D",
        },
        {
            valor: "pasar",
            etiqueta: "Pasar",
            icono: <ChevronsRight size={28} aria-hidden />,
            colorBg: "bg-vote-pasar-soft",
            colorBgSelected: "bg-vote-pasar",
            colorBorder: "border-vote-pasar",
            colorText: "text-vote-pasar",
            atajo: "P",
        },
    ];

    return (
        <div
            role="group"
            aria-label="Tu voto sobre esta afirmación"
            className="grid grid-cols-3 gap-2 sm:gap-3"
            data-testid="vote-buttons"
        >
            {opciones.map((o) => {
                const isSelected = seleccionado === o.valor;
                const isLoading = enviando && valorEnviando === o.valor;
                return (
                    <button
                        key={o.valor}
                        type="button"
                        onClick={() => onVotar(o.valor)}
                        disabled={disabled || enviando}
                        aria-pressed={isSelected}
                        aria-label={`${o.etiqueta} (atajo ${o.atajo})`}
                        data-testid={`vote-btn-${o.valor}`}
                        className={[
                            "min-h-[88px] sm:min-h-[104px] rounded-xl border-2 px-4 py-4 sm:py-5",
                            "flex flex-col items-center justify-center gap-2",
                            "transition-all duration-150 ease-out",
                            "focus-visible:ring-offset-2 focus-visible:ring-2",
                            "disabled:opacity-60 disabled:cursor-not-allowed",
                            isSelected
                                ? `${o.colorBgSelected} ${o.colorBorder} text-white shadow-md scale-[1.02]`
                                : `${o.colorBg} ${o.colorBorder} ${o.colorText} hover:scale-[1.01] hover:shadow-sm`,
                            isLoading ? "animate-pulse-soft" : "",
                        ].join(" ")}
                    >
                        {isLoading ? (
                            <LoadingSpinner size={24} label="Guardando voto" />
                        ) : (
                            o.icono
                        )}
                        <span className="text-lg sm:text-xl font-semibold">
                            {o.etiqueta}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
